/* =====================================================================
   sht-kit-customizer — Kit page customizer
   =====================================================================
   Responsibilities:
     - Track extra-camera count (stepper), NVR upgrade (radio), HDD
       upgrade or add (radio), and optional warehouse notes.
     - Recalculate live total + line-by-line breakdown on every change.
     - On submit, POST a multi-item add to /cart/add.js. Each line carries
       descriptive line item properties (prefixed with "_" so they're
       hidden from customer-facing cart UI but visible to order admin).
     - Refresh the cart drawer using the same contract as
       section-product-bundle.js (sht-cart-drwr.getSectionsToRender).

   Performance:
     - Script tag is deferred (set in section template).
     - No external deps; vanilla JS.
     - Event delegation keeps listener count to one per instance.
   ===================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('sht-kit-customizer')) return;

  // ---------- helpers ------------------------------------------------------

  function formatMoney(amountCents, moneyFormat) {
    var amount = (amountCents / 100).toFixed(2);
    var fmt = moneyFormat || '${{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amountCents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(amountCents / 100).toString());
  }

  function updateHeaderCartCount(count) {
    var numEl = document.querySelector('#headerCartStatus .cart-count-number');
    if (numEl) numEl.textContent = String(count);
    var wrapEl = document.querySelector('#headerCartStatus .header__cart-count');
    if (wrapEl) {
      wrapEl.className = wrapEl.className.replace(/header-cart-count-\d+/g, '').trim() + ' header-cart-count-' + String(count);
      wrapEl.style.display = count > 0 ? '' : 'none';
    }
  }

  // ---------- cart transport ----------------------------------------------
  // Shopify throttles the cart endpoints per IP and answers HTTP 429 with an
  // HTML body (not JSON); once tripped the block persists for minutes. A
  // customized add fires N+2 cart requests in a burst where the native
  // Add-to-cart fires exactly one, so it trips the limiter far more easily —
  // that is the "button greys out, nothing happens, cart unchanged" report.
  // Retry 429/5xx with backoff (honouring Retry-After) and abort a stalled
  // request, so a hiccup no longer costs the sale and the spinner can never
  // sit there forever.
  var CART_TIMEOUT_MS = 15000;
  var CART_MAX_ATTEMPTS = 4;

  function sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  async function cartFetch(url, options) {
    var lastRes = null;
    for (var attempt = 1; attempt <= CART_MAX_ATTEMPTS; attempt++) {
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, CART_TIMEOUT_MS) : null;
      try {
        var res = await fetch(url, ctrl
          ? Object.assign({}, options, { signal: ctrl.signal })
          : options);
        // Anything the shop actually answered (2xx, or a 4xx other than 429)
        // is a real answer — hand it back and let the caller decide.
        if (res.status !== 429 && res.status < 500) return res;
        lastRes = res;
      } catch (e) {
        // Network drop, or our own 15s abort.
        if (attempt === CART_MAX_ATTEMPTS) throw e;
        lastRes = null;
      } finally {
        if (timer) clearTimeout(timer);
      }
      if (attempt === CART_MAX_ATTEMPTS) break;
      var retryAfter = lastRes ? parseFloat(lastRes.headers.get('Retry-After')) : NaN;
      var waitMs = isFinite(retryAfter)
        ? Math.min(retryAfter * 1000, 4000)
        : 400 * Math.pow(2, attempt - 1);   // 400 / 800 / 1600 ms
      console.warn('[kitcust] cart request throttled — retry ' + attempt + ' in ' + waitMs + 'ms');
      await sleep(waitMs);
    }
    return lastRes;
  }

  // ---------- component ----------------------------------------------------

  class KitCustomizer extends HTMLElement {
    constructor() {
      super();
      this._bound = false;
    }

    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      // Cache dataset
      this.cfg = {
        sectionId: this.dataset.sectionId,
        kitVariantId: this.dataset.kitVariant,
        kitPrice: parseInt(this.dataset.kitPrice, 10) || 0,
        kitTitle: this.dataset.kitTitle || '',
        kitSku: this.dataset.kitSku || '',
        moneyFormat: this.dataset.moneyFormat || '${{amount}}',
        baseCameraHandle: this.dataset.baseCameraHandle,
        baseCameraVariantId: this.dataset.baseCameraVariant,
        baseCameraPrice: parseInt(this.dataset.baseCameraPrice, 10) || 0,
        baseCameraCount: parseInt(this.dataset.baseCameraCount, 10) || 0,
        baseCameraTitle: this.dataset.baseCameraTitle || '',
        baseCameraSku: this.dataset.baseCameraSku || '',
        baseCamera2Handle: this.dataset.baseCamera2Handle || '',
        baseCamera2VariantId: this.dataset.baseCamera2Variant || '',
        baseCamera2Price: parseInt(this.dataset.baseCamera2Price, 10) || 0,
        baseCamera2Count: parseInt(this.dataset.baseCamera2Count, 10) || 0,
        baseCamera2Title: this.dataset.baseCamera2Title || '',
        baseCamera2Sku: this.dataset.baseCamera2Sku || '',
        baseNvrHandle: this.dataset.baseNvrHandle,
        baseNvrChannels: parseInt(this.dataset.baseNvrChannels, 10) || 0,
        baseNvrTitle: this.dataset.baseNvrTitle || '',
        baseHddHandle: this.dataset.baseHddHandle,
        baseHddCapacity: parseInt(this.dataset.baseHddCapacity, 10) || 0,
        baseHddTitle: this.dataset.baseHddTitle || '',
        hddMode: this.dataset.hddMode || 'add',
        nvrMode: this.dataset.nvrMode || 'upgrade',
        kitVendor: this.dataset.kitVendor || '',
        extraCameraMax: parseInt(this.dataset.extraCameraMax, 10) || 0,
        // Bulk Savings display (attrs absent for Trade customers / feature off)
        volPercent: parseFloat(this.dataset.volPercent) || 0,
        volThresholdCents: parseInt(this.dataset.volThresholdCents, 10) || 0,
      };

      // State
      this.state = {
        extraCameras: 0,
        extraCameras2: 0,  // 2nd base camera in mixed kits; stays 0 for single-camera kits
        nvrUpgrade: null,  // { variantId, price, targetHandle, targetTitle, fromTitle, fromChannels, toChannels }
        hddUpgrade: null,  // { variantId, price, targetHandle, targetTitle, fromTitle, fromCapacity, toCapacity, mode }
        notes: '',
      };

      this._cacheRefs();
      this._mountInline();
      this._bindEvents();
      this._initStickyBridge();
      this._initNoHddGuard();

      var self = this;
      this.monitorSelector = (this.refs.monitorPanel && window.BundleMonitorSelector)
        ? new window.BundleMonitorSelector(this.refs.monitorPanel, { createChoice: function (select) { return self._createPriceChoice(select); }, onChange: function () { self._recalc(); } }) : null;
      this.accessorySelector = (this.refs.accessoryPanel && window.BundleAccessorySelector)
        ? new window.BundleAccessorySelector(this.refs.accessoryPanel, { onChange: function () { self._recalc(); } })
        : null;
      this.cableSelector = (this.refs.cablePanel && window.BundleCableSelector)
        ? new window.BundleCableSelector(this.refs.cablePanel, { onChange: function () { self._recalc(); } })
        : null;

      this._recalc();
    }

    _cacheRefs() {
      this.refs = {
        camMinus: this.querySelector('.js-kitcust-cam-minus'),
        camPlus: this.querySelector('.js-kitcust-cam-plus'),
        camInput: this.querySelector('.js-kitcust-cam-input'),
        camPrice: this.querySelector('.js-kitcust-cam-price'),
        cam2Minus: this.querySelector('.js-kitcust-cam2-minus'),
        cam2Plus: this.querySelector('.js-kitcust-cam2-plus'),
        cam2Input: this.querySelector('.js-kitcust-cam2-input'),
        cam2Price: this.querySelector('.js-kitcust-cam2-price'),
        cam2Usage: this.querySelector('.js-kitcust-cam2-usage'),
        notes: this.querySelector('.js-kitcust-notes'),
        total: this.querySelector('.js-kitcust-total'),
        breakdownList: this.querySelector('.js-kitcust-breakdown-list'),
        cta: this.querySelector('.js-kitcust-add'),
        ctaLabel: this.querySelector('.js-kitcust-cta-label'),
        spinner: this.querySelector('.kitcust__spinner'),
        error: this.querySelector('.js-kitcust-error'),
        channelWarn: this.querySelector('.js-kitcust-channel-warn'),
        camUsage: this.querySelector('.js-kitcust-cam-usage'),
        bulk: this.querySelector('.js-kitcust-bulk'),
        bulkPrice: this.querySelector('.js-kitcust-bulk-price'),
      };
      this.refs.accessoryPanel = this.querySelector('[data-accessory-panel]');
      this.refs.monitorPanel = this.querySelector('[data-monitor-panel]');
      this.refs.cablePanel = this.querySelector('[data-cable-panel]');
      // No-HDD reminder modal (only rendered for add-mode kits with the
      // feature enabled — refs.nohddOverlay doubles as the feature switch).
      this.refs.nohddOverlay = this.querySelector('.js-kitcust-nohdd');
      this.refs.nohddPrimary = this.querySelector('.js-kitcust-nohdd-primary');
      this.refs.nohddContinue = this.querySelector('.js-kitcust-nohdd-continue');
      this.refs.nohddClose = this.querySelector('.js-kitcust-nohdd-close');
    }

    // Move only HDD/NVR controls. Camera extras, cables, accessories, notes,
    // and the detailed price breakdown remain in the original section below.
    _mountInline() {
      this._optionRoot = this;
      var slot = document.querySelector('[data-kit-inline-slot]');
      if (!slot) return;
      var self = this;
      this._mainRoot = slot.closest('.main-product');
      this._mainForm = this._mainRoot.querySelector('form.js-main-product-form');
      if (!this._mainForm) return;
      this._inline = true;
      this._mainButton = this._mainForm.querySelector('.js-product-form-submit-btn');
      this._quantity = this._mainForm.querySelector('[name="quantity"]');
      var variants = this.querySelector('.js-kitcust-variants');
      this._kitVariants = variants ? JSON.parse(variants.textContent) : [];
      this._variantObserver = new MutationObserver(function () {
        if (self._refreshKitVariant()) self._recalc();
      });
      this._variantObserver.observe(this._mainForm, { attributes: true, attributeFilter: ['value'], childList: true, subtree: true });
      var panel = document.createElement('div');
      panel.className = 'kitcust--inline';
      slot.appendChild(panel);
      this._optionRoot = panel;
      this._inlinePanel = panel;
      this._mainRoot.classList.add('has-inline-kit');
      this._mergeKitSummary();

      var rows = this.querySelector('.kitcust__rows');
      var hdd = rows.querySelector('[data-row="hdd"], [data-row="hdd-fixed"]');
      var nvr = rows.querySelector('[data-row="nvr"]');
      var optionRows = document.createElement('div');
      optionRows.className = 'kitcust__rows';
      panel.appendChild(optionRows);
      if (nvr) optionRows.appendChild(nvr);
      if (hdd) optionRows.appendChild(hdd);
      if (this.refs.monitorPanel) optionRows.appendChild(this.refs.monitorPanel);
      if (!hdd && !nvr && !this.refs.monitorPanel) panel.hidden = true;

      if (hdd && this.cfg.hddMode !== 'fixed') {
        var title = hdd.querySelector('.kitcust__row-title');
        title.textContent = this.cfg.hddMode === 'add' ? 'Hard drive' : 'Hard drive upgrade';
        var source = hdd.querySelector('.kitcust__options');
        if (source) {
          var select = document.createElement('select');
          select.className = 'kitcust__select kitcust__hdd-select';
          select.id = 'kit-hdd-select-' + this.cfg.sectionId;
          select.setAttribute('aria-labelledby', hdd.getAttribute('aria-labelledby'));
          this._hddInputs = Array.from(source.querySelectorAll('.kitcust__opt-input'));
          this._hddInputs.forEach(function (input, index) {
            var option = document.createElement('option');
            option.value = String(index);
            var price = formatMoney(parseInt(input.dataset.upgradePrice, 10) || 0, self.cfg.moneyFormat);
            var cap = input.dataset.toCapacity;
            option.textContent = !input.dataset.upgradeVariant
              ? (self.cfg.hddMode === 'add' ? 'No HDD — No recording' : self.cfg.baseHddCapacity + 'TB — Included')
              : cap + 'TB — +' + price;
            option.dataset.label = input.dataset.upgradeVariant ? cap + 'TB'
              : (self.cfg.hddMode === 'add' ? 'No HDD — No recording' : self.cfg.baseHddCapacity + 'TB');
            option.dataset.amount = input.dataset.upgradeVariant ? '+' + price : '';
            option.dataset.included = !input.dataset.upgradeVariant && self.cfg.hddMode !== 'add' ? 'Included' : '';
            if (input.disabled) option.textContent += ' · Unavailable';
            option.disabled = input.disabled;
            option.selected = input.checked;
            select.appendChild(option);
          });
          source.hidden = true;
          hdd.appendChild(select);
          this._hddSelect = select;
          var help = document.createElement('p');
          help.className = 'kitcust__storage-help';
          help.id = select.id + '-help';
          select.setAttribute('aria-describedby', help.id);
          hdd.appendChild(help);
          this._storageHelp = help;
          select.addEventListener('change', function () {
            var input = self._hddInputs[Number(select.value)];
            if (!input || input.disabled) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          this._hddChoice = this._createPriceChoice(select);
        }
      }
      if (nvr && this.cfg.nvrMode !== 'fixed') {
        nvr.querySelector('.kitcust__row-title').textContent = 'NVR recorder';
        var nvrSource = nvr.querySelector('.kitcust__options');
        var nvrSelect = document.createElement('select');
        nvrSelect.className = 'kitcust__select kitcust__nvr-select';
        nvrSelect.id = 'kit-nvr-select-' + this.cfg.sectionId;
        nvrSelect.setAttribute('aria-labelledby', nvr.getAttribute('aria-labelledby'));
        this._nvrInputs = Array.from(nvrSource.querySelectorAll('.kitcust__opt-input'));
        this._nvrInputs.forEach(function (input, index) {
          var card = input.closest('.kitcust__opt');
          var model = input.dataset.targetModel || self.dataset.baseNvrModel || self.cfg.baseNvrTitle;
          card.querySelector('.kitcust__opt-title').textContent = model;
          var option = document.createElement('option');
          option.value = String(index);
          option.textContent = card.dataset.channels + 'CH — ' + (input.dataset.upgradeVariant
            ? '+' + formatMoney(parseInt(input.dataset.upgradePrice, 10) || 0, self.cfg.moneyFormat) : 'Included') + ' · ' + model;
          option.dataset.label = card.dataset.channels + 'CH';
          option.dataset.amount = input.dataset.upgradeVariant ? '+' + formatMoney(parseInt(input.dataset.upgradePrice, 10) || 0, self.cfg.moneyFormat) : '';
          option.dataset.included = input.dataset.upgradeVariant ? '' : 'Included';
          option.dataset.detail = model;
          if (input.disabled) option.textContent += ' · Unavailable';
          option.selected = input.checked;
          option.disabled = input.disabled;
          nvrSelect.appendChild(option);
        });
        nvrSource.hidden = true;
        nvrSelect.disabled = this._nvrInputs.length === 1;
        nvr.appendChild(nvrSelect);
        this._nvrSelect = nvrSelect;
        nvrSelect.addEventListener('change', function () {
          var input = self._nvrInputs[Number(nvrSelect.value)];
          if (!input || input.disabled) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this._nvrChoice = this._createPriceChoice(nvrSelect);
      }
      function refreshQuantity() { self._recalc(); }
      if (this._quantity) {
        this._quantity.addEventListener('input', refreshQuantity);
        this._quantity.addEventListener('change', refreshQuantity);
      }
      this._mainForm.addEventListener('click', function (event) {
        if (event.target.closest('.js-quantity-btn')) requestAnimationFrame(refreshQuantity);
      });
      // All purchase buttons use the same configured items and quantity.
      document.addEventListener('click', function (event) {
        var button = event.target.closest('.js-product-form-submit-btn');
        if (!button || !(self._mainForm.contains(button) || button.closest('sht-prd-frm-sticky'))) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!button.disabled) self._submit();
      }, true);
      document.addEventListener('submit', function (event) {
        if (event.target !== self._mainForm) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!self._mainButton.disabled) self._submit();
      }, true);
    }

    // Native option menus cannot style just the price. Keep the select as the
    // state adapter and use the same keyboard-accessible picker for HDD and NVR.
    _createPriceChoice(select) {
      var wrap = document.createElement('div');
      wrap.className = 'kitcust__choice';
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'kitcust__select kitcust__choice-trigger';
      button.id = select.id + '-trigger';
      button.disabled = select.disabled;
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      var value = document.createElement('span');
      value.className = 'kitcust__choice-line';
      value.id = select.id + '-value';
      button.setAttribute('aria-labelledby', select.getAttribute('aria-labelledby') + ' ' + value.id);
      if (select.hasAttribute('aria-describedby')) button.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
      button.appendChild(value);
      var menu = document.createElement('div');
      menu.className = 'kitcust__choice-menu';
      menu.id = select.id + '-list';
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-labelledby', select.getAttribute('aria-labelledby'));
      menu.hidden = true;
      button.setAttribute('aria-controls', menu.id);
      wrap.appendChild(button);
      wrap.appendChild(menu);
      select.after(wrap);
      select.hidden = true;
      select.classList.remove('kitcust__select');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      function appendText(parent, className, text) {
        var span = document.createElement('span');
        span.className = className;
        span.textContent = text;
        parent.appendChild(span);
      }
      function render(parent, option, detail) {
        parent.textContent = '';
        appendText(parent, 'kitcust__choice-label', option.dataset.label);
        if (option.dataset.amount) appendText(parent, 'kitcust__choice-price', option.dataset.amount);
        else if (option.dataset.included) appendText(parent, 'kitcust__choice-included', option.dataset.included);
        if (detail && option.dataset.detail) appendText(parent, 'kitcust__choice-model', option.dataset.detail);
        if (option.disabled) appendText(parent, 'kitcust__choice-model', 'Unavailable');
      }
      var options = Array.from(select.options).map(function (option) {
        var item = document.createElement('div');
        item.className = 'kitcust__choice-option';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-disabled', String(option.disabled));
        item.tabIndex = -1;
        item.dataset.value = option.value;
        render(item, option, true);
        menu.appendChild(item);
        item.addEventListener('click', function () { choose(item); });
        return item;
      });
      function sync() {
        var option = select.selectedOptions[0];
        if (!option) return;
        render(value, option, false);
        button.title = option.textContent;
        options.forEach(function (item) { item.setAttribute('aria-selected', String(item.dataset.value === select.value)); });
      }
      function close(restoreFocus) {
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        if (restoreFocus) button.focus();
      }
      function open() {
        if (button.disabled) return;
        menu.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        var item = options.find(function (el) { return el.dataset.value === select.value && el.getAttribute('aria-disabled') !== 'true'; })
          || options.find(function (el) { return el.getAttribute('aria-disabled') !== 'true'; });
        if (item) item.focus({ preventScroll: true });
      }
      function choose(item) {
        if (item.getAttribute('aria-disabled') === 'true') return;
        select.value = item.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        close(true);
      }
      button.addEventListener('click', function () { if (menu.hidden) open(); else close(false); });
      button.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); open(); }
      });
      var search = '', searchAt = 0;
      menu.addEventListener('keydown', function (event) {
        var available = options.filter(function (el) { return el.getAttribute('aria-disabled') !== 'true'; });
        var index = available.indexOf(document.activeElement);
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
        if (event.key === 'Tab') { close(true); return; }
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); if (index >= 0) choose(available[index]); return; }
        var next = null;
        if (event.key === 'ArrowDown') next = available[(index + 1) % available.length];
        if (event.key === 'ArrowUp') next = available[(index - 1 + available.length) % available.length];
        if (event.key === 'Home') next = available[0];
        if (event.key === 'End') next = available[available.length - 1];
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          search = Date.now() - searchAt > 700 ? event.key : search + event.key;
          searchAt = Date.now();
          next = available.find(function (el) { return el.textContent.toLowerCase().startsWith(search.toLowerCase()); });
        }
        if (next) { event.preventDefault(); next.focus(); }
      });
      wrap.addEventListener('focusout', function (event) { if (!wrap.contains(event.relatedTarget)) close(false); });
      document.addEventListener('pointerdown', function (event) { if (!wrap.contains(event.target)) close(false); });
      select.addEventListener('change', sync);
      sync();
      return { sync: sync, button: button };
    }

    _syncMainPrice(total, bulkTotal, bulkQualifies, hasCustom) {
      if (!this._inline) return;
      var priceArea = this._mainRoot.querySelector('.product__prices');
      var original = priceArea && priceArea.querySelector('[id^="productPrice-"]');
      if (!original) return;
      var active = hasCustom || this._kitQuantity() > 1;
      this._mainRoot.classList.toggle('has-kit-configured-price', active);
      // The existing bulk-price skin has high-specificity display rules.
      // Save/restore its inline display rather than leaving two totals visible.
      if (!this._basePriceDisplay) this._basePriceDisplay = new WeakMap();
      var displays = this._basePriceDisplay;
      priceArea.querySelectorAll(':scope > [id^="productPrice-"], :scope > .pdp-volume-hint').forEach(function (element) {
        if (active) {
          if (!displays.has(element)) displays.set(element, { value: element.style.getPropertyValue('display'), priority: element.style.getPropertyPriority('display') });
          element.style.setProperty('display', 'none', 'important');
        } else if (displays.has(element)) {
          var saved = displays.get(element);
          if (saved.value) element.style.setProperty('display', saved.value, saved.priority);
          else element.style.removeProperty('display');
          displays.delete(element);
        }
      });
      if (!this._mainPricing) {
        var region = document.createElement('div');
        region.className = 'kitcust__main-pricing';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        region.innerHTML = '<div class="kitcust__main-price-line"><span class="kitcust__main-amount"></span><span class="kitcust__quantity-label"></span></div>' +
          '<div class="kitcust__main-bulk"><strong class="kitcust__main-bulk-amount"></strong><span class="kitcust__main-bulk-label">Bulk Price</span><span class="kitcust__main-bulk-note"></span></div>' +
          '<p class="kitcust__customer-price-note" hidden></p>';
        priceArea.insertBefore(region, original);
        this._mainPricing = region;
      }
      this._mainPricing.hidden = !active;
      if (!active) return;
      var currency = this.dataset.currency || 'AUD';
      var currencyCode = this.dataset.currencyCodeEnabled === 'true' ? ' ' + currency : '';
      function money(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(cents / 100) + currencyCode;
      }
      this._mainPricing.querySelector('.kitcust__main-amount').textContent = money(total);
      this._mainPricing.querySelector('.kitcust__quantity-label').textContent = this._kitQuantity() > 1 ? 'Total for ' + this._kitQuantity() + ' kits' : '';
      var bulk = this._mainPricing.querySelector('.kitcust__main-bulk');
      bulk.hidden = !bulkQualifies;
      var amount = bulk.querySelector('.kitcust__main-bulk-amount');
      // Preserve the server's audience / MAP gate; no price is revealed if
      // the existing customizer was only allowed to show the discount percent.
      amount.hidden = !this.refs.bulkPrice;
      amount.textContent = this.refs.bulkPrice ? money(bulkTotal) : '';
      var percent = Math.round(this.cfg.volPercent) + '%';
      bulk.querySelector('.kitcust__main-bulk-note').textContent = this.refs.bulkPrice
        ? percent + ' off ' + money(this.cfg.volThresholdCents).replace(currencyCode, '').replace(/\.00$/, '') + '+ · Auto at checkout'
        : 'Qualifies for ' + percent + ' off at checkout.';
      var customerNote = this._mainPricing.querySelector('.kitcust__customer-price-note');
      var trade = original.querySelector('.trade-price-block');
      var member = original.querySelector('.member-price-block');
      customerNote.hidden = bulkQualifies || (!trade && !member);
      customerNote.textContent = trade ? 'Eligible Trade discounts are applied at checkout.' : 'Eligible member discounts are applied at checkout.';
    }

    _kitQuantity() {
      return this._inline && this._quantity ? Math.max(1, parseInt(this._quantity.value, 10) || 1) : 1;
    }

    _refreshKitVariant() {
      if (!this._inline) return false;
      var input = this._mainForm.querySelector('[name="id"]');
      if (!input || String(input.value) === String(this.cfg.kitVariantId)) return false;
      var variant = this._kitVariants.find(function (item) { return String(item.id) === String(input.value); });
      if (!variant) return false;
      this.cfg.kitVariantId = String(variant.id);
      this.cfg.kitPrice = Number(variant.price);
      this.cfg.kitSku = variant.sku || '';
      return true;
    }

    _mergeKitSummary() {
      var inclusions = this.querySelector('.kitcust__inclusions');
      var breakdown = this.querySelector('.js-kitcust-breakdown');
      var baseName = breakdown && breakdown.querySelector('.kitcust__breakdown-kit .kitcust__breakdown-name');
      if (!inclusions || !baseName) return;
      this.classList.add('kitcust--merged-summary');
      var heading = breakdown.querySelector('.kitcust__breakdown-label');
      if (heading) heading.textContent = 'Kit contents & price';
      var label = inclusions.querySelector('.kitcust__inclusions-label');
      var hasCableNote = label && /cabl/i.test(label.textContent);
      if (label) label.textContent = 'Base kit includes';
      inclusions.setAttribute('aria-label', 'Base kit contents');
      inclusions.querySelectorAll('[data-kit-compact]').forEach(function (item) {
        item.textContent = item.dataset.kitCompact;
      });
      baseName.textContent = '';
      // Use a block container for the inclusion list, keeping the original price node.
      var contents = document.createElement('div');
      contents.className = baseName.className;
      baseName.replaceWith(contents);
      contents.appendChild(inclusions);
      if (hasCableNote) {
        var note = document.createElement('p');
        note.className = 'kitcust__cable-note';
        note.textContent = 'Cables are sold separately. Choose one per camera above.';
        breakdown.appendChild(note);
      }
    }

    _syncInline() {
      if (!this._inline) return;
      if (this._hddSelect) {
        this._hddSelect.value = String(this._hddInputs.findIndex(function (input) { return input.checked; }));
        var noDrive = this.cfg.hddMode === 'add' && !this.state.hddUpgrade;
        this._storageHelp.classList.toggle('is-warning', noDrive);
        this._storageHelp.textContent = noDrive ? ''
          : (this.cfg.hddMode === 'upgrade' ? 'Upgrade difference only; replaces the included drive.' : '');
        this._storageHelp.hidden = !this._storageHelp.textContent;
      }
      if (this._nvrSelect) {
        this._nvrSelect.value = String(this._nvrInputs.findIndex(function (input) { return input.checked; }));
      }
      if (this._hddChoice) this._hddChoice.sync();
      if (this._nvrChoice) this._nvrChoice.sync();
    }

    _bindEvents() {
      var self = this;

      // Camera stepper (primary base camera)
      if (this.refs.camMinus) {
        this.refs.camMinus.addEventListener('click', function () { self._stepCam(-1); });
      }
      if (this.refs.camPlus) {
        this.refs.camPlus.addEventListener('click', function () { self._stepCam(1); });
      }
      if (this.refs.camInput) {
        this.refs.camInput.addEventListener('change', function (e) {
          var v = parseInt(e.target.value, 10);
          if (isNaN(v) || v < 0) v = 0;
          if (v > self.cfg.extraCameraMax) v = self.cfg.extraCameraMax;
          e.target.value = String(v);
          self.state.extraCameras = v;
          self._updateStepperButtons();
          self._recalc();
        });
        this.refs.camInput.addEventListener('input', function (e) {
          var v = parseInt(e.target.value, 10);
          if (!isNaN(v) && v >= 0 && v <= self.cfg.extraCameraMax) {
            self.state.extraCameras = v;
            self._updateStepperButtons();
            self._recalc();
          }
        });
      }

      // Camera stepper (2nd base camera — mirrors primary; only wired when
      // the mixed-kit fieldset rendered).
      if (this.refs.cam2Minus) {
        this.refs.cam2Minus.addEventListener('click', function () { self._stepCam2(-1); });
      }
      if (this.refs.cam2Plus) {
        this.refs.cam2Plus.addEventListener('click', function () { self._stepCam2(1); });
      }
      if (this.refs.cam2Input) {
        this.refs.cam2Input.addEventListener('change', function (e) {
          var v = parseInt(e.target.value, 10);
          if (isNaN(v) || v < 0) v = 0;
          if (v > self.cfg.extraCameraMax) v = self.cfg.extraCameraMax;
          e.target.value = String(v);
          self.state.extraCameras2 = v;
          self._updateStepperButtons();
          self._recalc();
        });
        this.refs.cam2Input.addEventListener('input', function (e) {
          var v = parseInt(e.target.value, 10);
          if (!isNaN(v) && v >= 0 && v <= self.cfg.extraCameraMax) {
            self.state.extraCameras2 = v;
            self._updateStepperButtons();
            self._recalc();
          }
        });
      }

      // NVR / HDD radio changes (event delegation)
      var onOptionChange = function (e) {
        var t = e.target;
        if (!t.classList || !t.classList.contains('kitcust__opt-input')) return;
        var row = t.dataset.row;
        if (row === 'nvr') {
          self._setNvr(t);
        } else if (row === 'hdd') {
          self._setHdd(t);
        }
        self._updateSelectionVisuals(row);
        self._recalc();
      };
      this.addEventListener('change', onOptionChange);
      if (this._optionRoot !== this) this._optionRoot.addEventListener('change', onOptionChange);

      // Notes
      if (this.refs.notes) {
        this.refs.notes.addEventListener('input', function (e) {
          self.state.notes = e.target.value || '';
        });
      }

      // CTA
      if (this.refs.cta) {
        this.refs.cta.addEventListener('click', function () { self._submit(); });
      }
    }

    _stepCam(delta) {
      var n = this.state.extraCameras + delta;
      if (n < 0) n = 0;
      if (n > this.cfg.extraCameraMax) n = this.cfg.extraCameraMax;
      this.state.extraCameras = n;
      if (this.refs.camInput) this.refs.camInput.value = String(n);
      this._updateStepperButtons();
      this._recalc();
    }

    _stepCam2(delta) {
      var n = this.state.extraCameras2 + delta;
      if (n < 0) n = 0;
      if (n > this.cfg.extraCameraMax) n = this.cfg.extraCameraMax;
      this.state.extraCameras2 = n;
      if (this.refs.cam2Input) this.refs.cam2Input.value = String(n);
      this._updateStepperButtons();
      this._recalc();
    }

    _updateStepperButtons() {
      if (this.refs.camMinus) this.refs.camMinus.disabled = this.state.extraCameras <= 0;
      if (this.refs.camPlus) this.refs.camPlus.disabled = this.state.extraCameras >= this.cfg.extraCameraMax;
      if (this.refs.cam2Minus) this.refs.cam2Minus.disabled = this.state.extraCameras2 <= 0;
      if (this.refs.cam2Plus) this.refs.cam2Plus.disabled = this.state.extraCameras2 >= this.cfg.extraCameraMax;
    }

    _setNvr(input) {
      var vid = input.dataset.upgradeVariant;
      if (!vid || this.cfg.nvrMode === 'fixed') {
        this.state.nvrUpgrade = null;
        return;
      }
      this.state.nvrUpgrade = {
        variantId: vid,
        price: parseInt(input.dataset.upgradePrice, 10) || 0,
        targetHandle: input.dataset.targetHandle || '',
        targetTitle: input.dataset.targetTitle || '',
        fromTitle: input.dataset.fromTitle || '',
        fromChannels: parseInt(input.dataset.fromChannels, 10) || 0,
        targetModel: input.dataset.targetModel || input.dataset.targetTitle || '',
        toChannels: parseInt(input.dataset.toChannels, 10) || 0,
      };
    }

    _setHdd(input) {
      if (this.cfg.hddMode === 'fixed') {
        this.state.hddUpgrade = null;
        return;
      }
      var vid = input.dataset.upgradeVariant;
      if (!vid) {
        this.state.hddUpgrade = null;
        return;
      }
      this.state.hddUpgrade = {
        variantId: vid,
        price: parseInt(input.dataset.upgradePrice, 10) || 0,
        targetHandle: input.dataset.targetHandle || '',
        targetTitle: input.dataset.targetTitle || '',
        fromTitle: input.dataset.fromTitle || '',
        fromCapacity: parseInt(input.dataset.fromCapacity, 10) || 0,
        toCapacity: parseInt(input.dataset.toCapacity, 10) || 0,
        mode: input.dataset.mode || 'upgrade',
      };
    }

    _updateSelectionVisuals(row) {
      var opts = this._optionRoot.querySelectorAll('.kitcust__opt[data-row="' + row + '"]');
      opts.forEach(function (opt) {
        var inp = opt.querySelector('.kitcust__opt-input');
        if (inp && inp.checked) {
          opt.classList.add('is-selected');
        } else {
          opt.classList.remove('is-selected');
        }
      });
    }

    _recalc() {
      this._refreshKitVariant();
      if (this.cfg.hddMode === 'fixed') this.state.hddUpgrade = null;
      if (this.cfg.nvrMode === 'fixed') this.state.nvrUpgrade = null;
      var total = this.cfg.kitPrice;
      var camPriceCents = this.state.extraCameras * this.cfg.baseCameraPrice;
      var camPrice2Cents = this.state.extraCameras2 * this.cfg.baseCamera2Price;
      total += camPriceCents + camPrice2Cents;
      if (this.state.nvrUpgrade) total += this.state.nvrUpgrade.price;
      if (this.state.hddUpgrade) total += this.state.hddUpgrade.price;
      var accTotal = this.accessorySelector ? this.accessorySelector.getTotal() : 0;
      var cableTotal = this.cableSelector ? this.cableSelector.getTotal() : 0;
      total += accTotal + cableTotal;
      total *= this._kitQuantity();
      total += this.monitorSelector ? this.monitorSelector.getTotal() : 0;
      this._syncInline();

      // Update stepper price labels
      if (this.refs.camPrice) {
        this.refs.camPrice.textContent = '+' + formatMoney(camPriceCents, this.cfg.moneyFormat);
      }
      if (this.refs.cam2Price) {
        this.refs.cam2Price.textContent = '+' + formatMoney(camPrice2Cents, this.cfg.moneyFormat);
      }

      // Total
      if (this.refs.total) {
        this.refs.total.textContent = formatMoney(total, this.cfg.moneyFormat);
      }

      // Bulk Savings price — real payable amount once the configured total
      // crosses the threshold. Element only exists for eligible audiences
      // (non-Trade); the price sub-element is absent for MAP-restricted
      // guests (percent-only copy). Same rounding as the Liquid side.
      var bulkQualifies = false;
      var bulkTotal = total;
      if (this.refs.bulk && this.cfg.volThresholdCents > 0) {
        bulkQualifies = total >= this.cfg.volThresholdCents;
        if (bulkQualifies) {
          bulkTotal = total - Math.round(total * this.cfg.volPercent / 100);
          if (this.refs.bulkPrice) {
            this.refs.bulkPrice.textContent = formatMoney(bulkTotal, this.cfg.moneyFormat);
          }
        }
        this.refs.bulk.hidden = !bulkQualifies;
      }

      // Breakdown — rebuild the dynamic list (keep the kit base line, append others)
      if (this.refs.breakdownList) {
        var baseLinePrice = this.refs.breakdownList.querySelector('.kitcust__breakdown-kit .kitcust__breakdown-value');
        if (baseLinePrice) baseLinePrice.textContent = formatMoney(this.cfg.kitPrice, this.cfg.moneyFormat);
        // Keep first <li> (kit base line) only; remove anything we appended last time
        var items = this.refs.breakdownList.querySelectorAll('.kitcust__breakdown-item--upgrade');
        items.forEach(function (i) { i.remove(); });

        if (this.state.extraCameras > 0) {
          this._appendBreakdownLine(
            this.state.extraCameras + ' × ' + (this.cfg.baseCameraSku || this.cfg.baseCameraTitle),
            camPriceCents,
          );
        }
        if (this.state.extraCameras2 > 0) {
          this._appendBreakdownLine(
            this.state.extraCameras2 + ' × ' + (this.cfg.baseCamera2Sku || this.cfg.baseCamera2Title),
            camPrice2Cents,
          );
        }
        if (this.state.nvrUpgrade) {
          this._appendBreakdownLine(
            'NVR upgrade: ' + this.state.nvrUpgrade.fromChannels + 'ch → ' +
              this.state.nvrUpgrade.toChannels + 'ch · ' + this.state.nvrUpgrade.targetModel,
            this.state.nvrUpgrade.price,
          );
        }
        if (this.state.hddUpgrade) {
          var hddLabel = (this.state.hddUpgrade.mode === 'upgrade'
              ? 'HDD upgrade: ' + this.state.hddUpgrade.fromCapacity + 'TB → ' + this.state.hddUpgrade.toCapacity + 'TB'
              : 'Add HDD: ' + this.state.hddUpgrade.toCapacity + 'TB');
          this._appendBreakdownLine(hddLabel, this.state.hddUpgrade.price);
        }
        if (this.accessorySelector) {
          this.accessorySelector.getLines().forEach(function (l) {
            this._appendBreakdownLine(l.label + ' × ' + l.quantity, l.price * l.quantity);
          }, this);
        }
        if (this.cableSelector) {
          this.cableSelector.getLines().forEach(function (l) {
            this._appendBreakdownLine('Cable: ' + l.label + ' × ' + l.quantity, l.price * l.quantity);
          }, this);
        }
      }

      if (this.refs.breakdownList && this.monitorSelector) {
        this.monitorSelector.getLines().forEach(function (line) {
          var row = document.createElement('li'); row.className = 'kitcust__breakdown-item kitcust__breakdown-item--upgrade';
          var name = document.createElement('span'); name.className = 'kitcust__breakdown-name'; name.textContent = line.label;
          var price = document.createElement('span'); price.className = 'kitcust__breakdown-value'; price.textContent = '+' + formatMoney(line.price, this.cfg.moneyFormat);
          row.appendChild(name); row.appendChild(price); this.refs.breakdownList.appendChild(row);
        }, this);
      }

      // CTA label
      var hasCustom = this.state.extraCameras > 0 || this.state.extraCameras2 > 0 ||
        !!this.state.nvrUpgrade || !!this.state.hddUpgrade ||
        (this.monitorSelector && this.monitorSelector.getLines().length > 0) ||
        (this.accessorySelector && this.accessorySelector.getLines().length > 0) ||
        (this.cableSelector && this.cableSelector.getLines().length > 0);
      var ctaText = hasCustom
        ? ((this.refs.cta && this.refs.cta.dataset.customLabel) || 'Add customized kit to cart')
        : ((this.refs.cta && this.refs.cta.dataset.defaultLabel) || 'Add to cart');
      if (this.refs.ctaLabel) {
        this.refs.ctaLabel.textContent = ctaText;
      }
      var stickyPriceText = (bulkQualifies && this.refs.bulkPrice)
        ? formatMoney(bulkTotal, this.cfg.moneyFormat) + ' (' + Math.round(this.cfg.volPercent) + '% off)'
        : formatMoney(total, this.cfg.moneyFormat);
      this._syncSticky(this._inline || !!hasCustom, ctaText + ' · ' + stickyPriceText);
      this._syncMainPrice(total, bulkTotal, bulkQualifies, hasCustom);

      this._checkChannels();
      this._hideError();
    }

    // Channel sanity check — advisory only, never blocks add-to-cart.
    // Warns when total cameras (base + extras) exceed the selected NVR's
    // channels, and highlights the smallest NVR option that fits.
    _checkChannels() {
      var totalCams = this.cfg.baseCameraCount + this.cfg.baseCamera2Count +
        this.state.extraCameras + this.state.extraCameras2;
      var channels = this.state.nvrUpgrade
        ? this.state.nvrUpgrade.toChannels
        : this.cfg.baseNvrChannels;
      var over = channels > 0 && totalCams > channels;

      // Keep the cable selector's cameras-count in sync (drives its
      // "N cables / M cameras" strip and smart default quantity).
      if (this.cableSelector && typeof this.cableSelector.setCameraCount === 'function') {
        this.cableSelector.setCameraCount(totalCams);
      }

      // Live usage chip next to the camera stepper(s). Shown whenever either
      // stepper has extras > 0; the total reflects both.
      var anyExtras = this.state.extraCameras > 0 || this.state.extraCameras2 > 0;
      if (this.refs.camUsage) {
        if (anyExtras && channels > 0) {
          this.refs.camUsage.textContent = totalCams + ' cameras / ' + channels + 'ch NVR';
          this.refs.camUsage.classList.toggle('is-over', over);
          this.refs.camUsage.hidden = false;
        } else {
          this.refs.camUsage.hidden = true;
        }
      }
      if (this.refs.cam2Usage) {
        if (anyExtras && channels > 0) {
          this.refs.cam2Usage.textContent = totalCams + ' cameras / ' + channels + 'ch NVR';
          this.refs.cam2Usage.classList.toggle('is-over', over);
          this.refs.cam2Usage.hidden = false;
        } else {
          this.refs.cam2Usage.hidden = true;
        }
      }

      // Highlight the smallest NVR option that covers the camera count
      var best = null;
      var bestCh = 0;
      this._optionRoot.querySelectorAll('.kitcust__opt[data-row="nvr"]').forEach(function (opt) {
        opt.classList.remove('kitcust__opt--suggested');
        var ch = parseInt(opt.dataset.channels, 10) || 0;
        var input = opt.querySelector('.kitcust__opt-input');
        if (input && input.disabled) return;
        if (ch >= totalCams && (!best || ch < bestCh)) { best = opt; bestCh = ch; }
      });

      if (this.refs.channelWarn) {
        if (over) {
          var msg;
          if (best) {
            msg = totalCams + ' cameras selected — the ' + channels + 'ch NVR records a maximum of ' +
              channels + '. Choose the ' + bestCh + 'ch option.';
            best.classList.add('kitcust__opt--suggested');
          } else {
            msg = totalCams + ' cameras selected — this exceeds the largest NVR available here. ' +
              'Reduce the extra cameras, or contact us for a larger recorder.';
          }
          this.refs.channelWarn.textContent = msg;
          this.refs.channelWarn.hidden = false;
        } else {
          this.refs.channelWarn.hidden = true;
        }
      }
    }

    _appendBreakdownLine(name, priceCents) {
      var li = document.createElement('li');
      li.className = 'kitcust__breakdown-item kitcust__breakdown-item--upgrade';
      var nameSpan = document.createElement('span');
      nameSpan.className = 'kitcust__breakdown-name';
      nameSpan.textContent = name;
      var valSpan = document.createElement('span');
      valSpan.className = 'kitcust__breakdown-value';
      valSpan.textContent = formatMoney(priceCents, this.cfg.moneyFormat);
      li.appendChild(nameSpan);
      li.appendChild(valSpan);
      this.refs.breakdownList.appendChild(li);
    }

    _showError(msg) {
      this._showErrorToast(msg);
      if (!this.refs.error) return;
      this.refs.error.textContent = msg;
      this.refs.error.hidden = false;
    }

    _hideError() {
      this._hideErrorToast();
      if (!this.refs.error) return;
      this.refs.error.textContent = '';
      this.refs.error.hidden = true;
    }

    // The in-section error <p> lives in the customizer footer — up to ~2500px
    // below the fold when the shopper submitted from the sticky bar at the top
    // of the page. Without this toast a failed add is indistinguishable from
    // "the button did nothing", which is exactly how it got reported.
    // Portaled to <body> for the same reason the no-HDD modal is: below 1280px
    // the product-toc accordion display:none's this whole section, and a
    // position:fixed child of a hidden ancestor never renders.
    _showErrorToast(msg) {
      try {
        var el = document.querySelector('.kitcust__toast');
        if (!el) {
          el = document.createElement('div');
          el.className = 'kitcust__toast';
          el.setAttribute('role', 'alert');
          document.body.appendChild(el);
        }
        el.textContent = msg;
        el.hidden = false;
        var self = this;
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(function () { self._hideErrorToast(); }, 8000);
      } catch (e) { /* a broken toast must never block add-to-cart */ }
    }

    _hideErrorToast() {
      clearTimeout(this._toastTimer);
      var el = document.querySelector('.kitcust__toast');
      if (el) el.hidden = true;
    }

    // ---------- No-HDD reminder modal --------------------------------------
    // Everything here is fail-open: a bug in this path must never block
    // add-to-cart, so callers wrap in try/catch and fall through.

    _shouldWarnNoHdd() {
      if (!this.refs.nohddOverlay) return false;        // feature off / upgrade mode
      if (this._nohddDismissed) return false;           // "Continue" chosen earlier
      if (this.cfg.hddMode !== 'add') return false;
      if (this.state.hddUpgrade) return false;          // a drive IS selected
      // The page must actually offer at least one real drive to add.
      return !!this._optionRoot.querySelector('.kitcust__opt-input[data-row="hdd"][value]:not([value=""])');
    }

    _openNoHddModal(ctx) {
      this._nohddCtx = ctx || {};
      var primary = this.refs.nohddPrimary;
      var rec = this._optionRoot.querySelector('.kitcust__opt--recommended .kitcust__opt-input');
      if (rec && !rec.disabled) {
        this._nohddRec = rec;
        var price = formatMoney(parseInt(rec.dataset.upgradePrice, 10) || 0, this.cfg.moneyFormat);
        primary.textContent = (primary.dataset.addLabel || 'Add {capacity}TB HDD ({price})')
          .replace('{capacity}', rec.dataset.toCapacity || '')
          .replace('{price}', price);
      } else {
        this._nohddRec = null;
        primary.textContent = primary.dataset.chooseLabel || 'Choose a hard drive';
      }
      this._nohddLastFocus = document.activeElement;
      this.refs.nohddOverlay.hidden = false;
      primary.focus();
    }

    _closeNoHddModal() {
      if (!this.refs.nohddOverlay) return;
      this.refs.nohddOverlay.hidden = true;
      this._nohddCtx = null;
      if (this._nohddLastFocus && this._nohddLastFocus.focus) {
        try { this._nohddLastFocus.focus(); } catch (e) { /* noop */ }
      }
      this._nohddLastFocus = null;
    }

    _initNoHddGuard() {
      if (!this.refs.nohddOverlay) return;              // feature off / upgrade mode
      var self = this;

      // Portal the modal to <body>. Below 1280px the product-toc accordion
      // collapses this whole section with display:none (ptoc-acc--closed), and
      // a position:fixed overlay inside a hidden ancestor never renders — the
      // guard then swallows Add-to-cart taps with no visible modal. Moving the
      // overlay out of the section keeps it renderable regardless of the
      // accordion state; all listeners bind to the overlay itself so the
      // existing refs stay valid.
      if (this.refs.nohddOverlay.parentElement !== document.body) {
        document.body.appendChild(this.refs.nohddOverlay);
      }
      this._nohddDismissed = false;  // per-pageview, all entry points
      this._nohddBypass = false;     // one-shot flag for programmatic re-click
      this._nohddRec = null;
      this._nohddCtx = null;
      this._nohddLastFocus = null;

      // Intercept NATIVE add-to-cart buttons (main form + sticky bar when the
      // sticky bridge is not active). Same capture-phase pattern as
      // _initStickyBridge — theme JS is not modified. Scoped to THIS kit via
      // the form's variant id, which excludes quick-view / quick-add buttons
      // for other products; payment buttons never carry the class at all.
      document.addEventListener('click', function (ev) {
        try {
          if (self._nohddBypass) return;
          var btn = ev.target.closest('.js-product-form-submit-btn');
          if (!btn) return;
          var form = btn.form || btn.closest('form');
          var idInput = form ? form.querySelector('input[name="id"]') : null;
          if (!idInput || String(idInput.value) !== String(self.cfg.kitVariantId)) return;
          // While customized, the sticky bridge owns sticky clicks and routes
          // them into _submit(), whose own pre-check shows the modal.
          if (self._stickyActive && btn.closest('sht-prd-frm-sticky')) return;
          if (!self._shouldWarnNoHdd()) return;
          ev.preventDefault();
          ev.stopImmediatePropagation();
          self._openNoHddModal({ nativeBtn: btn });
        } catch (e) { /* fail-open: let the native flow proceed */ }
      }, true);

      // Primary: one-click add the recommended drive (or scroll to the HDD
      // row when the kit has none). Adding a drive always goes through the
      // customizer's multi-item _submit(), regardless of the entry point.
      this.refs.nohddPrimary.addEventListener('click', function () {
        var rec = self._nohddRec;
        self._closeNoHddModal();
        if (rec) {
          rec.click();  // native change event → _setHdd + visuals + recalc
          self._submit({ skipNoHddCheck: true });
        } else {
          // The configurator used to sit behind a <details> that had to be
          // opened first. That fold was removed 2026-08-27 — the body is
          // always in the flow now, so we can scroll straight to the drive.
          var opt = self._optionRoot.querySelector('.kitcust__opt-input[data-row="hdd"][value]:not([value=""])');
          var card = opt ? opt.closest('.kitcust__opt') : null;
          if (self._hddSelect) {
            self._hddSelect.scrollIntoView({ behavior: 'smooth', block: 'center' });
            self._hddSelect.focus();
          } else if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });

      // Continue without HDD: remember the choice for this pageview, then
      // resume whichever flow was interrupted.
      this.refs.nohddContinue.addEventListener('click', function () {
        self._nohddDismissed = true;
        var ctx = self._nohddCtx || {};
        self._closeNoHddModal();
        if (ctx.nativeBtn) {
          self._nohddBypass = true;  // let the re-click reach the native handler
          try { ctx.nativeBtn.click(); } finally { self._nohddBypass = false; }
        } else {
          self._submit({ skipNoHddCheck: true });
        }
      });

      // Dismiss (×, overlay, Esc) = cancel this add-to-cart, no flag set.
      this.refs.nohddClose.addEventListener('click', function () { self._closeNoHddModal(); });
      this.refs.nohddOverlay.addEventListener('click', function (ev) {
        if (ev.target === self.refs.nohddOverlay) self._closeNoHddModal();
      });
      document.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape' && self.refs.nohddOverlay && !self.refs.nohddOverlay.hidden) {
          self._closeNoHddModal();
        }
      });
    }

    async _submit(opts) {
      opts = opts || {};
      this._refreshKitVariant();
      if (this.refs.cta.disabled) return;
      if (this._inline && this._mainButton.disabled) return;
      var optionAvailability = window.SmarketCameraOptions;
      if (optionAvailability && optionAvailability.available && !optionAvailability.available(this.cfg.kitVariantId)) {
        this._showError('No lens / colour combination is currently available for this kit.');
        return;
      }
      if (this.cfg.hddMode === 'fixed') this.state.hddUpgrade = null;
      if (this.cfg.nvrMode === 'fixed') this.state.nvrUpgrade = null;

      // No-HDD reminder: intercept before building items. Fail-open — any
      // error in the modal path falls through to a normal submit.
      if (!opts.skipNoHddCheck) {
        try {
          if (this._shouldWarnNoHdd()) {
            this._openNoHddModal({ resume: 'submit' });
            return;
          }
        } catch (e) { /* fall through */ }
      }

      var items = [];
      var kitContextLabel = this.cfg.kitTitle + (this.cfg.kitSku ? ' (' + this.cfg.kitSku + ')' : '');
      var cameraOptions = window.SmarketCameraOptions;
      var cameraContext = cameraOptions ? cameraOptions.context(this.cfg.kitVariantId) : '';
      if (cameraContext) kitContextLabel += ' [' + cameraContext + ']';

      // 1. Kit itself
      var kitItem = { id: parseInt(this.cfg.kitVariantId, 10), quantity: 1 };
      if (this.state.notes && this.state.notes.trim()) {
        kitItem.properties = { _kit_notes: this.state.notes.trim().slice(0, 500) };
      }
      if (cameraOptions) cameraOptions.apply(kitItem);
      items.push(kitItem);

      // 2. Extra cameras — primary base camera (if any)
      if (this.state.extraCameras > 0 && this.cfg.baseCameraVariantId) {
        items.push({
          id: parseInt(this.cfg.baseCameraVariantId, 10),
          quantity: this.state.extraCameras,
          properties: {
            _kit_context: kitContextLabel,
            _addon_type: 'Extra cameras (beyond base ' + this.cfg.baseCameraCount + ')',
          },
        });
      }

      // 2b. Extra cameras — 2nd base camera in mixed kits (if any)
      if (this.state.extraCameras2 > 0 && this.cfg.baseCamera2VariantId) {
        items.push({
          id: parseInt(this.cfg.baseCamera2VariantId, 10),
          quantity: this.state.extraCameras2,
          properties: {
            _kit_context: kitContextLabel,
            _addon_type: 'Extra cameras (beyond base ' + this.cfg.baseCamera2Count + ')',
          },
        });
      }

      // Only the kit and camera lines receive camera preferences.
      if (cameraOptions) items.forEach(function (item) { cameraOptions.apply(item); });

      // 3. NVR upgrade (if any)
      if (this.state.nvrUpgrade) {
        items.push({
          id: parseInt(this.state.nvrUpgrade.variantId, 10),
          quantity: 1,
          properties: {
            _kit_context: kitContextLabel,
            _upgrade_type: 'NVR: ' + this.state.nvrUpgrade.fromChannels + 'ch → ' + this.state.nvrUpgrade.toChannels + 'ch',
            _replaces: this.state.nvrUpgrade.fromTitle,
            _with: this.state.nvrUpgrade.targetTitle,
          },
        });
      }

      // 4. HDD upgrade or add (if any)
      if (this.state.hddUpgrade) {
        var hddProps = {
          _kit_context: kitContextLabel,
        };
        if (this.state.hddUpgrade.mode === 'upgrade') {
          hddProps._upgrade_type = 'HDD: ' + this.state.hddUpgrade.fromCapacity + 'TB → ' + this.state.hddUpgrade.toCapacity + 'TB';
          hddProps._replaces = this.state.hddUpgrade.fromTitle;
          hddProps._with = this.state.hddUpgrade.targetTitle;
        } else {
          hddProps._addon_type = 'Add HDD: ' + this.state.hddUpgrade.toCapacity + 'TB';
          hddProps._with = this.state.hddUpgrade.targetTitle;
        }
        items.push({
          id: parseInt(this.state.hddUpgrade.variantId, 10),
          quantity: 1,
          properties: hddProps,
        });
      }

      // 5. Accessories (each its own line item)
      if (this.accessorySelector) {
        this.accessorySelector.getCartItems().forEach(function (it) {
          items.push({ id: it.id, quantity: it.quantity, properties: { _kit_context: kitContextLabel } });
        });
      }
      // 6. Cable (each length its own line item)
      if (this.cableSelector) {
        this.cableSelector.getCartItems().forEach(function (it) {
          items.push({ id: it.id, quantity: it.quantity, properties: { _kit_context: kitContextLabel } });
        });
      }

      var kitQuantity = this._kitQuantity();
      items.forEach(function (item) { item.quantity *= kitQuantity; });
      // A screen is a single optional purchase, independent of kit/camera quantity.
      if (this.monitorSelector) this.monitorSelector.getCartItems().forEach(function (item) { items.push(item); });

      // ===== Submit — sequential single-item adds via theme's own transport ====
      // Why this shape:
      //   The merchant theme's native SHTProductForm.onSubmitHandler builds a
      //   FormData from the product form (one item) and POSTs to /cart/add.js
      //   with `X-Requested-With: XMLHttpRequest` + `Accept: application/javascript`
      //   (see SHTHelper.fetchConfigHTTP). It then hands the response to
      //   renderContents which updates the drawer + opens the notification.
      //
      //   We replay that EXACT transport — one POST per kit line — so each
      //   add looks indistinguishable from a normal main-product Add to the
      //   shop's edge / WAF / app stack. Three previous transport variants
      //   were tried and rejected:
      //     • JSON multi-item + sections → 403 from edge
      //     • FormData multi-item with `items[i][...]` brackets → 200 but
      //       items silently dropped (Shopify multi-item parser doesn't pick
      //       up indexed brackets reliably on this endpoint)
      //     • JSON multi-item no sections + separate GET → drawer shows empty
      //       (the GET refresh raced or returned stale state)
      //
      //   Only the LAST add carries `sections` + `sections_url` — that way
      //   the final response body has the rendered drawer + notification
      //   HTML reflecting the FULL cart (all preceding adds included).
      this._setLoading(true);
      this._hideError();

      var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var cartAddUrl = (window.routes && window.routes.cart_add_url) || (rootUrl + 'cart/add.js');

      // Kit already in the cart (e.g. added moments ago via the native
      // button) + this submit carries add-ons → the shopper is supplementing
      // that kit, not buying a second one. Drop the kit line and add only
      // the add-ons (Clarity recordings showed shoppers ending up with two
      // kits and deleting one). A plain re-add (no add-ons) still adds
      // another kit. Fail-open: if the cart check errors, keep the full list.
      if (!this._inline && items.length > 1) {
        try {
          var preRes = await cartFetch(rootUrl + 'cart.js');
          if (preRes && preRes.ok) {
            var preCart = await preRes.json();
            var kitIdNum = parseInt(this.cfg.kitVariantId, 10);
            if (preCart.items.some(function (l) { return l.variant_id === kitIdNum && (!cameraOptions || cameraOptions.sameConfiguration(l, kitItem)); })) {
              items = items.filter(function (it) { return it.id !== kitIdNum; });
              console.log('[kitcust] kit already in cart — adding add-ons only');
            }
          }
        } catch (e) { /* keep full list */ }
      }

      var cartNotification = document.querySelector('sht-cart-noti');
      var cartDrawer = document.querySelector('sht-cart-drwr');
      var cartDrawerForm = document.querySelector('sht-cart-drwr-frm');

      // Pre-compute the sections list to attach to the last add. These are
      // THEME methods, called before the try/finally below — a throw here used
      // to leave the button disabled with a spinner, no error and no cart
      // change. Losing the section list only costs the inline drawer refresh
      // (the adds themselves still go through), so swallow and carry on.
      var sectionsList = '';
      try {
        var sectionsToRender = [];
        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          sectionsToRender = sectionsToRender.concat(cartNotification.getSectionsToRender());
        }
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          sectionsToRender = sectionsToRender.concat(cartDrawer.getSectionsToRender());
        }
        sectionsList = Array.from(new Set(
          sectionsToRender.map(function (s) { return s.id; }).filter(Boolean)
        )).join(',');
      } catch (e) {
        console.warn('[kitcust] getSectionsToRender failed — adding without sections:', e);
      }

      console.groupCollapsed('[kitcust] submit', items.length, 'item(s)');
      console.log('  cartAddUrl:', cartAddUrl);
      console.log('  sectionsList:', sectionsList);
      console.log('  items:', JSON.parse(JSON.stringify(items)));

      try {
        var lastResponse = null;

        for (var idx = 0; idx < items.length; idx++) {
          var item = items[idx];
          var isLast = (idx === items.length - 1);

          var fd = new FormData();
          fd.append('id', String(item.id));
          fd.append('quantity', String(item.quantity));
          if (item.properties) {
            Object.keys(item.properties).forEach(function (k) {
              fd.append('properties[' + k + ']', String(item.properties[k]));
            });
          }
          if (isLast && sectionsList) {
            fd.append('sections', sectionsList);
            fd.append('sections_url', window.location.pathname);
          }

          // Echo the encoded payload for the network panel side-by-side.
          var dbgPayload = {};
          fd.forEach(function (v, k) { dbgPayload[k] = v; });
          console.log('  POST #' + idx + (isLast ? ' (last, with sections)' : ''), dbgPayload);

          var res = await cartFetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/javascript'
            },
            body: fd
          });

          var json = null;
          // cartFetch returns null only if every attempt was throttled away;
          // a throttled 429 body is HTML, so .json() rejects there too.
          try { json = res ? await res.json() : null; } catch (e) { /* body may be empty */ }

          console.log('  POST #' + idx + ' response:', res ? res.status : 'no response', json);

          if (!res || !res.ok || (json && json.status)) {
            var status = res ? res.status : 0;
            var msg = (json && (json.description || json.message))
                      || (status === 429
                          ? 'The cart is busy right now — please wait a few seconds and try again.'
                          : 'Add to cart failed (HTTP ' + status + '). Please try again.');
            console.error('[kitcust] add.js failed at item ' + idx + ':', status, json);
            console.groupEnd();
            this._showError(msg);
            return;
          }

          if (isLast) lastResponse = json;
        }

        console.log('  lastResponse has sections?',
                    !!(lastResponse && lastResponse.sections),
                    lastResponse && lastResponse.sections
                      ? Object.keys(lastResponse.sections)
                      : null);

        // Update ONLY the drawer DOM. We deliberately skip
        // cartNotification.renderContents() because its last line is
        // this.open() — which pops a toast. Kit adds may contain N line
        // items; the drawer is the right surface to show them all in one
        // place, and a redundant toast on top creates the "double popup"
        // the customer complained about.
        if (lastResponse && cartDrawerForm && typeof cartDrawerForm.renderContents === 'function') {
          try {
            cartDrawerForm.renderContents(lastResponse);
          } catch (e) {
            console.warn('[kitcust] cartDrawerForm.renderContents failed:', e);
          }
        }

        // Open the drawer explicitly (renderContents only updates DOM).
        try {
          if (window.shtCartDrawer && typeof window.shtCartDrawer.openDrawer === 'function') {
            var headerCartBtn = document.querySelector('.js-header-cart-status-btn')
                             || document.querySelector('#headerCartStatus')
                             || document.body;
            window.shtCartDrawer.openDrawer(headerCartBtn);
          }
        } catch (e) {
          console.warn('[kitcust] openDrawer failed:', e);
        }

        // Bubble events for analytics / other listeners.
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // Update header cart count from cart.js (source of truth).
        try {
          var cartRes = await cartFetch(rootUrl + 'cart.js');
          if (cartRes && cartRes.ok) {
            var cart = await cartRes.json();
            console.log('  post-submit cart.js:',
                        'item_count=' + cart.item_count,
                        'items=' + cart.items.length);
            updateHeaderCartCount(cart.item_count);
          }
        } catch (e) { /* noop */ }

        console.groupEnd();
        if (!this._inline) this._reset();
      } catch (err) {
        console.error('[kitcust] submit threw:', err);
        console.groupEnd();
        this._showError('Network error — please try again');
      } finally {
        this._setLoading(false);
      }
    }

    _setLoading(loading) {
      if (!this.refs.cta) return;
      if (this._inline && this._mainButton) {
        this._mainButton.disabled = loading;
        this._mainButton.classList.toggle('loading', loading);
        this._mainButton.setAttribute('aria-busy', String(loading));
      }
      if (loading) {
        this.refs.cta.classList.add('is-loading');
        this.refs.cta.disabled = true;
        if (this.refs.spinner) this.refs.spinner.hidden = false;
        if (this._stickyActive) this._setStickyLoading(true);
      } else {
        this.refs.cta.classList.remove('is-loading');
        this.refs.cta.disabled = false;
        if (this.refs.spinner) this.refs.spinner.hidden = true;
        this._setStickyLoading(false);
      }
    }

    // ---------- sticky-bar bridge ---------------------------------------
    // The theme's sticky bar "Add to cart" forwards to the MAIN product
    // form and would add ONLY the plain kit — silently dropping extra
    // cameras / NVR / HDD upgrades. While the kit is customized we relabel
    // the sticky button (with live total) and route its click to this
    // component's proven _submit() path. With no customization the native
    // behavior is untouched. Theme JS is NOT modified: the click is caught
    // in the CAPTURE phase on document, which runs before the sticky
    // button's own listener (SHTProductFormSticky).
    _initStickyBridge() {
      this._stickyBtn = document.querySelector('sht-prd-frm-sticky .js-product-form-submit-btn');
      this._stickyTxt = this._stickyBtn ? this._stickyBtn.querySelector('.js-product-form-submit-btn-text') : null;
      this._stickySpinner = document.querySelector('sht-prd-frm-sticky .js-product-form-spinner');
      this._stickyActive = false;
      this._stickyOrig = null;
      if (!this._stickyBtn || !this._stickyTxt) return;
      if (this._inline) return; // Unified inline purchase bridge owns both buttons.
      var self = this;
      document.addEventListener('click', function (ev) {
        if (!self._stickyActive) return;
        var btn = ev.target.closest('sht-prd-frm-sticky .js-product-form-submit-btn');
        if (!btn) return;
        ev.preventDefault();
        ev.stopPropagation();
        if (btn.disabled || btn.classList.contains('loading')) return;
        self._submit();
      }, true);
    }

    _syncSticky(active, labelText) {
      if (!this._stickyBtn || !this._stickyTxt) return;
      if (active) {
        if (this._stickyOrig === null) this._stickyOrig = this._stickyTxt.textContent.trim();
        this._stickyTxt.textContent = labelText;
      } else if (this._stickyActive && this._stickyOrig !== null) {
        this._stickyTxt.textContent = this._stickyOrig;
        this._stickyOrig = null;
      }
      this._stickyActive = active;
    }

    _setStickyLoading(loading) {
      if (!this._stickyBtn) return;
      if (loading) {
        this._stickyBtn.classList.add('loading');
        this._stickyBtn.setAttribute('disabled', 'disabled');
        if (this._stickySpinner) this._stickySpinner.classList.remove('hidden');
      } else {
        this._stickyBtn.classList.remove('loading');
        this._stickyBtn.removeAttribute('disabled');
        if (this._stickySpinner) this._stickySpinner.classList.add('hidden');
      }
    }

    _reset() {
      // Reset state to defaults (and update UI)
      this.state.extraCameras = 0;
      this.state.extraCameras2 = 0;
      this.state.nvrUpgrade = null;
      this.state.hddUpgrade = null;
      this.state.notes = '';

      if (this.refs.camInput) this.refs.camInput.value = '0';
      if (this.refs.cam2Input) this.refs.cam2Input.value = '0';
      if (this.refs.notes) this.refs.notes.value = '';

      // Reset all radios to base / none
      var allRadios = this._optionRoot.querySelectorAll('.kitcust__opt-input');
      allRadios.forEach(function (r) {
        // Base/none options have empty data-upgrade-variant
        if (!r.dataset.upgradeVariant) {
          r.checked = true;
        } else {
          r.checked = false;
        }
      });

      if (this.monitorSelector) this.monitorSelector.clear(false);
      if (this.accessorySelector) this.accessorySelector.clear();
      if (this.cableSelector) this.cableSelector.clear();

      this._updateStepperButtons();
      this._updateSelectionVisuals('nvr');
      this._updateSelectionVisuals('hdd');
      this._recalc();
    }
  }

  customElements.define('sht-kit-customizer', KitCustomizer);
})();
